/**
 * The asset picker's choices: the engine's built-in shapes (for props that
 * take them), then the content index's asset catalog, named like Unreal's
 * asset names (no extension) with the folder and size as detail.
 */
import type { ContentIndex } from "../../assets/content";
import { ENGINE_MESHES, engineShapeOf, SHAPE_ASSET_KIND } from "../../assets/engineAssets";
import { skinListing, skinsDetail } from "../../assets/skinSets";
import { assetDetail, assetLabel } from "../widgets/assetPickerModel";
import type { AssetOption } from "../widgets/WidgetEnvironment";
import type { AssetLookup } from "./inspectorModel";

/** The detail engine assets show where content shows its folder and size: they're built in. */
const ENGINE_DETAIL = "Engine";

/** What the picker lists: the content index's catalog and the engine assets' thumbnails. */
export type AssetSources = Pick<ContentIndex, "assets" | "engineThumbnails" | "skinSets">;

/** `url` turns a content-relative path (the asset's thumbnail) into one the page can load. */
export function assetOptions(
  { assets, engineThumbnails = {}, skinSets }: AssetSources,
  kinds: readonly string[],
  url: (path: string) => string,
): AssetOption[] {
  const engine = kinds.includes(SHAPE_ASSET_KIND)
    ? ENGINE_MESHES.map((mesh) => {
        const thumbnail = engineThumbnails[mesh.path];
        return {
          value: mesh.path,
          label: mesh.name,
          detail: ENGINE_DETAIL,
          ...(thumbnail !== undefined && { thumbnail: url(thumbnail) }),
        };
      })
    : [];
  // A skin set shows as its default model; its other skins are the Skin row's to offer.
  const listing = skinListing(skinSets);
  const content = assets
    .filter((asset) => kinds.includes(asset.kind) && listing.isListed(asset.path))
    .map((asset) => ({
      value: asset.path,
      label: assetLabel(asset.path),
      detail: `${assetDetail(asset)}${skinsDetail(listing, asset.path)}`,
      ...(asset.thumbnail !== undefined && { thumbnail: url(asset.thumbnail) }),
    }));
  return [...engine, ...content];
}

/**
 * What the content index says about a path: its asset kind (`shape` for the
 * engine's shapes, else the catalog's) and the skin set it's in.
 */
export function assetLookupIn({ assets, skinSets = [] }: AssetSources): AssetLookup {
  return {
    kindOf: (path) =>
      engineShapeOf(path) !== null
        ? SHAPE_ASSET_KIND
        : (assets.find((asset) => asset.path === path)?.kind ?? null),
    skinSetOf: (path) =>
      skinSets.find((set) => set.skins.some((skin) => skin.model === path)) ?? null,
  };
}
